// tabs/crafting.ts — client renderer for the PC sheet's Crafting tab.
//
// Raw catalog rows are fetched on demand. The browser's shared crafting plan
// and stock readers determine requirements, size rungs and availability.

import type { CachedCaster } from '../types';
import { hydrateSheetCraftingPayload } from '../crafting-selector';
import { displayRefusal } from '../k5-runtime';
import { scanSortable } from '../../catalog-list/ft-sortable';

interface CraftItem { name: string; missing: boolean }
/** magical = the requirement names an enchantment template (Universal Crafter waives every NON-magical one — crafting-waiver.ts). */
interface CraftReagent { label: string; missing: boolean; magical: boolean }
/** One size rung of a size-scalable recipe (engine-computed by the same plan builder the craft
 *  start consumes — options/api/creature_sheet_catalog_inc.php). */
interface CraftSize {
    refusal: string;
    size_id: string; name: string; is_default: boolean;
    reagents: CraftReagent[]; crafting_time_int: number; crafting_time_type: string; can_craft: boolean;
}
interface Recipe {
    refusal: string;
    id: string; name: string; item_type: string; required_tier: number;
    ability_dc: unknown; crafting_time_int: number; crafting_time_type: string;
    can_craft: boolean; tools: CraftItem[]; reagents: CraftReagent[];
    scalable: boolean; default_size: string; sizes: CraftSize[];
    /** Tools alone held / the plan buildable — what a Universal Crafter still needs when only non-magical reagents are missing. */
    tools_ok: boolean; plan_ok: boolean;
}

// Per-render state for the size picker's re-render (one recipe per family, 2026-09-08): picking a
// rung swaps the row's reagents / time / craft button for that rung's engine-computed values.
// Keyed by the crafting ROOT element, never module-global: two casters' sheets on one page must not
// share reagent availability or edit permission (Codex review 2026-09-08 #3).
interface CraftingRootState { recipes: Map<string, Recipe>; canEdit: boolean; waives: boolean }
const craftingRootState = new WeakMap<HTMLElement, CraftingRootState>();
const craftingInputs = new WeakMap<HTMLElement, Record<string, unknown>>();
const craftingPaints = new WeakMap<HTMLElement, number>();
interface CraftSkill {
    var: string; name: string; tab_icon_id: string; col_icon_id: string;
    tier_level: number; recipes: Recipe[];
}
interface CraftingPayload { skills: CraftSkill[]; can_edit: boolean; error?: string; message?: string }

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function textOf(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function numberOf(value: unknown): number {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
}

function booleanOf(value: unknown): boolean {
    return value === true || value === 1 || value === '1';
}

function craftItemOf(value: unknown): CraftItem | null {
    const row = recordOf(value);
    return row ? { name: textOf(row.name), missing: booleanOf(row.missing) } : null;
}

function craftReagentOf(value: unknown): CraftReagent | null {
    const row = recordOf(value);
    return row ? { label: textOf(row.label), missing: booleanOf(row.missing), magical: booleanOf(row.magical) } : null;
}

function compactMap<T>(value: unknown, reader: (entry: unknown) => T | null): T[] {
    return Array.isArray(value)
        ? value.map(reader).filter((entry): entry is T => entry !== null)
        : [];
}

function craftSizeOf(value: unknown): CraftSize | null {
    const row = recordOf(value);
    if (!row) return null;
    const size_id = textOf(row.size_id);
    if (size_id === '') return null;
    return {
        size_id, refusal: textOf(row.refusal),
        name: textOf(row.name),
        is_default: booleanOf(row.is_default),
        reagents: compactMap(row.reagents, craftReagentOf),
        crafting_time_int: numberOf(row.crafting_time_int),
        crafting_time_type: textOf(row.crafting_time_type),
        can_craft: booleanOf(row.can_craft),
    };
}

function recipeOf(value: unknown): Recipe | null {
    const row = recordOf(value);
    if (!row) return null;
    const sizes = compactMap(row.sizes, craftSizeOf);
    return {
        id: textOf(row.id), refusal: textOf(row.refusal),
        name: textOf(row.name),
        item_type: textOf(row.item_type),
        required_tier: numberOf(row.required_tier),
        ability_dc: row.ability_dc,
        crafting_time_int: numberOf(row.crafting_time_int),
        crafting_time_type: textOf(row.crafting_time_type),
        can_craft: booleanOf(row.can_craft),
        tools: compactMap(row.tools, craftItemOf),
        reagents: compactMap(row.reagents, craftReagentOf),
        scalable: booleanOf(row.scalable) && sizes.length > 0,
        default_size: textOf(row.default_size),
        sizes,
        // Plan and tool flags are supplied by the browser selector.
        tools_ok: booleanOf(row.tools_ok), plan_ok: booleanOf(row.plan_ok),
    };
}

/** The rung-specific view of a recipe: the selected size's reagents / time / affordability, or the
 *  plain recipe when no size (or the default rung) is selected. `waives` = the crafter holds Universal Crafter
 *  (crafting_waives_nonmagical_reagents): a missing NON-magical reagent is waived (shown as such, never blocks the craft). */
function recipeAtSize(r: Recipe, sizeId: string | null, waives = false): { reagents: CraftReagent[]; timeInt: number; timeType: string; canCraft: boolean; scaled: boolean; sizeId: string | null } {
    const size = sizeId && r.scalable ? r.sizes.find((s) => s.size_id === sizeId) ?? null : null;
    const view = !size || size.is_default
        ? { reagents: r.reagents, timeInt: r.crafting_time_int, timeType: r.crafting_time_type, canCraft: r.can_craft, scaled: false, sizeId: null }
        : { reagents: size.reagents, timeInt: size.crafting_time_int, timeType: size.crafting_time_type, canCraft: size.can_craft, scaled: true, sizeId: size.size_id };
    if (!waives || !r.tools_ok || !r.plan_ok || !view.reagents.some((x) => x.missing && !x.magical)) return view;
    const reagents = view.reagents.map((x) => (x.missing && !x.magical ? { ...x, missing: false, label: `${x.label} (waived)` } : x));
    return { ...view, reagents, canCraft: reagents.every((x) => !x.missing) };
}

function craftSkillOf(value: unknown): CraftSkill | null {
    const row = recordOf(value);
    if (!row) return null;
    return {
        var: textOf(row.var),
        name: textOf(row.name),
        tab_icon_id: textOf(row.tab_icon_id),
        col_icon_id: textOf(row.col_icon_id),
        tier_level: numberOf(row.tier_level),
        recipes: compactMap(row.recipes, recipeOf),
    };
}

function craftingPayloadOf(value: unknown): CraftingPayload {
    const payload = recordOf(value);
    if (!payload) return { skills: [], can_edit: false };
    const out: CraftingPayload = {
        skills: compactMap(payload.skills, craftSkillOf),
        can_edit: booleanOf(payload.can_edit),
    };
    const error = textOf(payload.error);
    const message = textOf(payload.message);
    if (error !== '') out.error = error;
    if (message !== '') out.message = message;
    return out;
}

/** data-bs-html tooltip title: prefix + escaped lines joined by <br> (escapeHtml also attr-safes quotes). */
function tooltipTitle(prefix: string, lines: string[]): string {
    return lines.length ? `${prefix}<br>${lines.join('<br>')}` : '';
}

function actionCell(r: Recipe, canEdit: boolean, sizeId: string | null = null, waives = false): string {
    const view = recipeAtSize(r, sizeId, waives);
    if (view.canCraft) {
        // A non-default rung rides the secondary choice slot (use_ability_variable_2 = the SizeType
        // uuid); the default rung is a plain craft, byte-identical to before the size picker existed.
        const call = view.scaled && view.sizeId
            ? `useAbilityVariable2('${escapeHtml(r.id)}', '', '${escapeHtml(view.sizeId)}');`
            : `useAbility('${escapeHtml(r.id)}');`;
        const attrs = canEdit ? ` onclick="${call}"` : ' disabled aria-disabled="true"';
        return `<button type="button" class="btn btn-primary btn-sm"${attrs}>${iconMap(r.item_type)}</button>`;
    }
    const parts: string[] = [];
    const missingTools = r.tools.filter((t) => t.missing).map((t) => escapeHtml(t.name));
    const missingReagents = view.reagents.filter((t) => t.missing).map((t) => escapeHtml(t.label));
    // iconMap(..., title=false) → bare <img>, NO tooltip wrapper. The outer span owns the
    // (rich, HTML) tooltip; letting iconMap emit its own data-bs-toggle="tooltip" would nest two
    // tooltips and the inner generic "Tools"/"Reagent" one wins on hover (the legacy emitted a plain img here).
    if (missingTools.length) {
        parts.push(`<span class="d-inline-block text-danger" data-bs-html="true" data-bs-toggle="tooltip" title="${tooltipTitle('Missing Tools:', missingTools)}">${iconMap('tools', 25, 25, false)}</span>`);
    }
    if (missingReagents.length) {
        parts.push(`<span class="d-inline-block text-danger" data-bs-html="true" data-bs-toggle="tooltip" title="${tooltipTitle('Missing Reagents:', missingReagents)}">${iconMap('reagents', 25, 25, false)}</span>`);
    }
    return `<div class="d-flex justify-content-center gap-2">${parts.join('')}</div>`;
}

/** Full required-list tooltip column (shown regardless of ownership; * marks missing). */
function requirementCell(iconId: string, label: string, items: { text: string; missing: boolean }[]): string {
    const lines = items.map((i) => `• ${escapeHtml(i.text)}${i.missing ? ' *' : ''}`);
    const title = items.length ? tooltipTitle(`Required ${label}:`, lines) : `No ${label.toLowerCase()} required`;
    // title=false → bare <img>: the outer span owns the tooltip; avoid the nested-tooltip clash (see actionCell).
    return `<span class="d-inline-block" data-bs-html="true" data-bs-toggle="tooltip" title="${title}">${iconMap(iconId, 25, 25, false)}</span>`;
}

/** The size picker for a scalable recipe (default rung preselected). Sits under the recipe name. */
function sizePicker(r: Recipe, sizeId: string | null): string {
    if (!r.scalable) return '';
    const selected = sizeId ?? r.sizes.find((s) => s.is_default)?.size_id ?? '';
    const options = r.sizes.map((s) => `<option value="${escapeHtml(s.size_id)}"${s.size_id === selected ? ' selected' : ''}>${escapeHtml(s.name)}${s.is_default ? ' (default)' : ''}</option>`).join('');
    return `<select class="form-select form-select-sm d-inline-block w-auto mt-1 ft-craft-size" style="height:30px;" aria-label="Craft size for ${escapeHtml(r.name)}" data-ft-craft-size="${escapeHtml(r.id)}">${options}</select>`;
}

/** The cells of a recipe row for a chosen size rung — everything between the <tr> tags, so a size
 *  change can rewrite the row in place without touching the table (ft-sortable stays mounted). */
function recipeRowCells(r: Recipe, canEdit: boolean, sizeId: string | null, waives = false): string {
    const view = recipeAtSize(r, sizeId, waives);
    const tools = r.tools.map((t) => ({ text: t.name, missing: t.missing }));
    const reagents = view.reagents.map((t) => ({ text: t.label, missing: t.missing }));
    const dc = (r.ability_dc === null || r.ability_dc === undefined || r.ability_dc === '') ? '—' : String(r.ability_dc);
    const dcSort = (r.ability_dc === null || r.ability_dc === undefined || r.ability_dc === '') ? '' : String(r.ability_dc);
    const timeType = view.timeInt === 1 ? view.timeType.slice(0, -1) : view.timeType;
    // Ingredient names hidden in the name cell so the global search (ft-sortable) matches by tool/
    // reagent too; data-sort keeps the NAME column sorting by recipe name only (not the hidden text).
    const ingredientText = [...r.tools.map((t) => t.name), ...view.reagents.map((t) => t.label)].join(' ');
    return `<td class="text-center">${(sizeId ? r.sizes.find(size => size.size_id === sizeId)?.refusal : r.refusal) ? displayRefusal(sizeId ? r.sizes.find(size => size.size_id === sizeId)?.refusal : r.refusal) : actionCell(r, canEdit, sizeId, waives)}</td>
        <td class="text-center">${requirementCell('tools', 'Tools', tools)}</td>
        <td class="text-center">${requirementCell('reagents', 'Reagents', reagents)}</td>
        <td class="text-left" data-sort="${escapeHtml(r.name)}"><span role="button" onclick="openItemInfo('${escapeHtml(r.id)}');">${escapeHtml(r.name)}</span><span class="visually-hidden"> ${escapeHtml(ingredientText)}</span>${r.scalable ? '<br>' + sizePicker(r, sizeId) : ''}</td>
        <td class="text-center" data-sort="${escapeHtml(String(r.required_tier))}">${iconMap('skill_tier_' + r.required_tier)}</td>
        <td class="text-center" data-sort="${escapeHtml(dcSort)}"><span class="badge text-light bg-dark border border-light">${escapeHtml(dc)}</span></td>
        <td class="text-right" data-sort="${escapeHtml(String(view.timeInt))}">${escapeHtml(String(view.timeInt))} ${escapeHtml(timeType)}</td>`;
}

function recipeRow(r: Recipe, canEdit: boolean, waives = false): string {
    return `<tr data-ft-craft-recipe="${escapeHtml(r.id)}">${recipeRowCells(r, canEdit, null, waives)}</tr>`;
}

/** Size picker change (event delegation on the crafting root): rewrite that recipe's row for the
 *  chosen rung. Bootstrap tooltips are delegation-based, so the new cells need no re-init. */
function onCraftSizeChange(e: Event): void {
    const select = e.target instanceof HTMLSelectElement ? e.target : null;
    if (!select || !select.matches('[data-ft-craft-size]')) return;
    const root = select.closest('.ft-pc-crafting-root') as HTMLElement | null;
    const state = root ? craftingRootState.get(root) : undefined;
    const recipeId = select.getAttribute('data-ft-craft-size') ?? '';
    const recipe = state?.recipes.get(recipeId);
    const row = select.closest('tr');
    if (!state || !recipe || !row) return;
    row.innerHTML = recipeRowCells(recipe, state.canEdit, select.value || null, state.waives);
}

function skillTable(skill: CraftSkill, canEdit: boolean, isFirst: boolean, waives = false): string {
    const rows = skill.recipes.map((r) => recipeRow(r, canEdit, waives)).join('');
    const searchId = `craft_search_${skill.var}`;
    // ft-sortable: click-to-sort columns + ONE global search box (data-ft-sortable-search) per tab.
    // Per-column filter boxes are suppressed (ft-no-filter on every <th>) since we want a single
    // search; icon/action columns are also ft-no-sort (nothing meaningful to order by).
    // The search <input> for this skill lives up in the tab-nav row (see craftSearchBar) to reuse the
    // empty space there; the table just points at it by id via data-ft-sortable-search.
    return `<div id="div_crafting_skill_table_${escapeHtml(skill.var)}" class="div_crafting_skill_table" style="display: ${isFirst ? 'block' : 'none'};">
        <table class="table table-hover table-sm align-middle ft-sortable" data-ft-sortable-search="${escapeHtml(searchId)}">
            <thead><tr>
                <th class="text-center ft-no-sort ft-no-filter" style="width:90px;">${iconMap(skill.col_icon_id)}</th>
                <th class="text-center ft-no-sort ft-no-filter" style="width:90px;">${iconMap('tools')}</th>
                <th class="text-center ft-no-sort ft-no-filter" style="width:90px;">${iconMap('reagents')}</th>
                <th class="ft-no-filter"></th>
                <th class="text-center ft-no-filter" style="width:70px;">${iconMap('requires_skill_tier')}</th>
                <th class="text-center ft-no-filter" style="width:50px;">${iconMap('54DC30E0-923C-4BBF-A8CC-570B0068E8A2')}</th>
                <th class="text-center ft-no-filter" style="width:90px;">${iconMap('crafting_time')}</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table>
    </div>`;
}

function navTabs(skills: CraftSkill[]): string {
    let tabs = '';
    skills.forEach((skill, i) => {
        // Pills (Bootstrap moves .active among siblings on click); the manual onclick swaps the table
        // AND the matching search box (one per skill, wired to its own table by id).
        tabs += `<li class="nav-item" role="presentation">
            <button class="nav-link ${i === 0 ? 'active' : ''}" id="tab_${escapeHtml(skill.var)}" data-bs-toggle="tab" type="button" role="tab"
                onclick="hideByClass('div_crafting_skill_table'); showEl('div_crafting_skill_table_${escapeHtml(skill.var)}'); hideByClass('ft-craft-search'); showEl('craft_search_wrap_${escapeHtml(skill.var)}');">
                ${iconMap(skill.tab_icon_id)}
            </button>
        </li>`;
    });
    return `<ul class="nav nav-pills mb-0 flex-nowrap" id="crafting_skill_icons_tabs_nav" role="tablist">${tabs}</ul>`;
}

/** Search inputs (one per skill, only the active shown) for the right side of the toolbar bar. Each
 *  input id matches its table's data-ft-sortable-search. Explicit height overrides the theme's global
 *  .form-control{height:50px} so it sits compactly in the bar. */
function craftSearchBar(skills: CraftSkill[]): string {
    // Mirrors the abilities-tab spyglass search look (position-relative + pe-4 input + overlaid
    // #search SVG, classes from table_item_player_character_inc.php). NOT the
    // `fate-tide-sheet-ability-search` class — that wires into the ability filter; crafting filters
    // via ft-sortable (data-ft-sortable-search by id).
    const inputs = skills.map((skill, i) => `<div id="craft_search_wrap_${escapeHtml(skill.var)}" class="ft-craft-search" style="display:${i === 0 ? 'block' : 'none'};">
            <div class="position-relative" style="max-width:260px;">
                <input type="search" id="craft_search_${escapeHtml(skill.var)}" class="form-control form-control-sm pe-4 w-100" style="height:34px;" placeholder="Search recipes or ingredients…" aria-label="Search ${escapeHtml(skill.name)} recipes" autocomplete="off">
                <svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-sheet-search-icon" aria-hidden="true"><use href="#search"></use></svg>
            </div>
        </div>`).join('');
    return `<div class="ft-craft-search-bar ms-auto ps-2">${inputs}</div>`;
}

function paintCrafting(root: HTMLElement, casterId: string, payload: CraftingPayload): void {
    const active = root.querySelector<HTMLButtonElement>('[data-bs-toggle="tab"].active')?.id;
    const searches = new Map(Array.from(root.querySelectorAll<HTMLInputElement>('input[type="search"]'), input => [input.id, input.value]));
    const sizes = new Map(Array.from(root.querySelectorAll<HTMLSelectElement>('[data-ft-craft-size]'), select => [select.dataset.ftCraftSize ?? '', select.value]));
    // Universal Crafter (crafting_waives_nonmagical_reagents) on the crafter's cached row — the encounter-overlays bundle's reader.
    const waives = (window as unknown as { ftCraftingWaiverHeld?: (id: string) => boolean | null }).ftCraftingWaiverHeld?.(casterId) === true;
    const state: CraftingRootState = { recipes: new Map<string, Recipe>(), canEdit: payload.can_edit, waives };
    payload.skills.forEach((s) => s.recipes.forEach((r) => state.recipes.set(r.id, r)));
    craftingRootState.set(root, state);
    const tables = payload.skills.map((s, i) => skillTable(s, payload.can_edit, i === 0, waives)).join('');
    root.innerHTML = `<div class="ft-pc-crafting row m-0 mb-2 pb-2 ml-1 mr-1 mt-2">
        <div class="col-lg-12 pl-2 pr-2"><div class="text-left">
            <div class="ft-crafting-toolbar d-flex justify-content-between align-items-center bg-dark bg-gradient rounded mb-2 p-2">
                ${navTabs(payload.skills)}
                ${craftSearchBar(payload.skills)}
            </div>
            ${tables}
        </div></div>
    </div>`;
    // Enhance the freshly-injected recipe tables: click-to-sort headers + per-tab search box.
    // Idempotent (each table guards on data-ft-sortable-mounted), so re-runs are safe.
    scanSortable();
    if (active) {
        const variable = active.replace(/^tab_/, '');
        root.querySelectorAll<HTMLButtonElement>('[data-bs-toggle="tab"]').forEach(button => button.classList.toggle('active', button.id === active));
        root.querySelectorAll<HTMLElement>('.div_crafting_skill_table').forEach(table => { table.style.display = table.id === 'div_crafting_skill_table_' + variable ? 'block' : 'none'; });
        root.querySelectorAll<HTMLElement>('.ft-craft-search').forEach(search => { search.style.display = search.id === 'craft_search_wrap_' + variable ? 'block' : 'none'; });
    }
    root.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach(input => {
        input.value = searches.get(input.id) ?? '';
        if (input.value) input.dispatchEvent(new Event('input', { bubbles: true }));
    });
    root.querySelectorAll<HTMLSelectElement>('[data-ft-craft-size]').forEach(select => {
        const selected = sizes.get(select.dataset.ftCraftSize ?? '');
        if (selected) { select.value = selected; select.dispatchEvent(new Event('change', { bubbles: true })); }
    });
}

export function renderCrafting(caster: CachedCaster, casterId: string, container: HTMLElement): void {
    // On-demand (legacy behavior). Crafting recipes are a DB-keyed catalog + live-inventory
    // availability that is NOT part of the core casterCache shape, so the compile is slow — we do
    // NOT fetch on mount. The list AJAXes in only when the user actually opens the Crafting tab
    // (or clicks the in-pane Load button). Idempotent: the stable .ft-pc-crafting-root wrapper means
    // WS surgical re-renders never wipe or re-fetch a loaded list.
    const existing = container.querySelector<HTMLElement>('.ft-pc-crafting-root');
    if (existing) {
        const inputs = craftingInputs.get(existing);
        if (inputs) {
            const paint = (craftingPaints.get(existing) ?? 0) + 1;
            craftingPaints.set(existing, paint);
            void hydrateSheetCraftingPayload(caster, inputs).then(payload => {
                if (craftingPaints.get(existing) === paint) paintCrafting(existing, casterId, craftingPayloadOf(payload));
            }).catch(error => { if (craftingPaints.get(existing) === paint) existing.innerHTML = displayRefusal(error); });
        }
        return;
    }

    container.innerHTML = `<div class="ft-pc-crafting-root"><div class="text-center p-4"><button type="button" class="btn btn-primary" data-ft-craft-load>Load crafting recipes</button></div></div>`;
    const root = container.querySelector('.ft-pc-crafting-root') as HTMLElement | null;
    if (!root) return;

    let loaded = false;
    async function load(): Promise<void> {
        if (loaded || !root) return;
        loaded = true;
        const paint = (craftingPaints.get(root) ?? 0) + 1;
        craftingPaints.set(root, paint);
        root.innerHTML = `<div class="text-center p-4 text-muted">Loading crafting…</div>`;
        let payload: CraftingPayload;
        try {
            const res = await fetch(`${BASE_URL}options/api/creature_sheet_catalog_inc.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ kind: 'recipes' }),
            });
            if (!res.ok) throw new Error('unported:crafting-display:catalog-read-failed');
            const inputs = await res.json() as Record<string, unknown>;
            craftingInputs.set(root, inputs);
            const current = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
            payload = craftingPayloadOf(await hydrateSheetCraftingPayload(current ?? caster, inputs));
        } catch (error) {
            if (craftingPaints.get(root) !== paint) return;
            loaded = false; // let the user retry by reopening the tab
            root.innerHTML = displayRefusal(error);
            return;
        }
        if (craftingPaints.get(root) !== paint) return;
        if (payload && payload.error) {
            // Structured server error (build/encode failure) — surface the message instead of the
            // generic "No crafting skills", so a real fault isn't misread as an empty catalog.
            loaded = false; // allow retry
            const msg = payload.message || payload.error;
            root.innerHTML = `<div class="text-center p-4 text-danger">Crafting failed to load: ${escapeHtml(msg)} — reopen the tab to retry.</div>`;
            return;
        }
        if (!payload || !Array.isArray(payload.skills) || payload.skills.length === 0) {
            root.innerHTML = `<div class="text-center p-4 text-muted">No crafting skills.</div>`;
            return;
        }
        paintCrafting(root, casterId, payload);
    }

    // In-pane Load button — covers the case where Crafting is the cookie-restored active tab at mount.
    root.addEventListener('click', (e) => {
        if (e.target instanceof Element && e.target.closest('[data-ft-craft-load]')) void load();
    });
    // Size picker on size-scalable recipes (delegated: rows are re-rendered in place).
    root.addEventListener('change', onCraftSizeChange);
    // One-click load on opening the Crafting tab (legacy on-demand). Uses the raw `click` event,
    // NOT shown.bs.tab, so it can NEVER fire on page load — only a genuine user click triggers it.
    const pane = container.closest('.tab-pane') as HTMLElement | null;
    const toggle = pane?.id
        ? document.querySelector<HTMLElement>(`[data-bs-target="#${CSS.escape(pane.id)}"]`)
        : null;
    toggle?.addEventListener('click', () => { void load(); });
}
